/**
 * Strips a URL down to what analytics may keep: origin, a path with IDs
 * replaced, and campaign parameters. Everything else goes, because the
 * address bar can hold sign-in tokens (`#access_token=…`, `?code=…`),
 * project and design IDs (`?open=…`, `/builder/<id>`), whole shared
 * designs (`#design=…`) and the ChatGPT ad click reference (`?oppref=…`).
 */

const KEPT_PARAMS = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref'])
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
/** Words in a Supabase Storage address that come before the bucket name. */
const STORAGE_VERBS = new Set(['public', 'sign', 'authenticated', 'info', 'list', 'upload', 'move', 'copy'])

export function scrubPath(pathname: string): string {
  const segments = pathname.split('/')
  // /storage/v1/object/[verb/]<bucket>/<user id>/<project id>/<image id>.png:
  // everything after the bucket names someone's files.
  const objectAt = segments.findIndex((seg, i) => seg === 'object' && segments[i - 1] === 'v1' && segments[i - 2] === 'storage')
  if (objectAt !== -1) {
    let bucketAt = objectAt + 1
    if (STORAGE_VERBS.has(segments[bucketAt] ?? '')) bucketAt++
    if (bucketAt < segments.length - 1) return [...segments.slice(0, bucketAt + 1), ':path'].join('/')
  }
  return segments
    .map((seg, i) => {
      if (UUID.test(seg)) return ':id'
      // /builder/<design id>
      if (i === 2 && segments[1] === 'builder' && seg !== '') return ':id'
      return seg
    })
    .join('/')
}

export function scrubUrl(url: string | null | undefined): string {
  if (!url) return ''
  let u: URL
  try {
    u = new URL(url)
  } catch {
    return ''
  }
  const kept = new URLSearchParams()
  for (const [k, v] of u.searchParams) {
    if (KEPT_PARAMS.has(k.toLowerCase())) kept.append(k, v)
  }
  const query = kept.toString()
  return `${u.origin}${scrubPath(u.pathname)}${query ? `?${query}` : ''}`
}
